// LLM generated - Model gpt-5.2-code
import { useEffect, useRef } from 'react';
import { Carousel } from '@mantine/carousel';
import { Loader, Stack } from '@mantine/core';
import Autoplay from 'embla-carousel-autoplay';
import Confetti from 'react-confetti';

import { createEnterKeyHandler } from '~/domains/auth/utils/formHelpers';
import IconGiftAurora from '~/domains/design-system/icons/IconGiftAurora';
import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import { PromoTicketCard } from '../PromoTicketCard/PromoTicketCard';
import { BonusCountdown } from '../../../../components/BonusCountdown/BonusCountdown';

import { useStyles } from './PromotionBonusModal.styles';
import type { BonusModalTemplateProps } from './types';

export function PromotionBonusModal({
  isOpen,
  isLoading,
  isCloseDisabled,
  onClose,
  onAction,
  error,
  title,
  description,
  bonuses,
  dataTestId,
  actionButtonTestId,
  actionButtonLabel,
}: BonusModalTemplateProps) {
  const { classes } = useStyles();
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }));

  useEffect(() => {
    if (!isOpen || isLoading) return;

    const handleKeyDown = createEnterKeyHandler(false, () => onAction?.());

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onAction]);

  return (
    <>
      <Confetti
        className={classes.confetti}
        recycle={false}
        numberOfPieces={1000}
        confettiSource={{
          x: window.innerWidth / 2 - 55,
          y: window.innerHeight / 2 - 100,
          w: 110,
          h: 100,
        }}
        tweenDuration={500}
        initialVelocityY={20}
        initialVelocityX={7.5}
      />
      <Dialog
        isOpen={isOpen}
        onClose={isCloseDisabled ? undefined : onClose}
        type={{
          desktop: 'modal',
          mobile: 'drawer',
        }}
        className={classes.container}
        dataTestId={dataTestId}
      >
        <DialogBody className={classes.dialogBody}>
          <div className={classes.content}>
            <div className={classes.headerSection}>
              <div className={classes.iconContainer}>
                <IconGiftAurora size={30} />
              </div>
              <div className={classes.textContent}>
                <Title
                  variant="title-xxl"
                  tag="h2"
                  className={classes.heading}
                  data-test-id={dt.promotions.bonusModal.title}
                >
                  {title}
                </Title>
                {description && (
                  <Text variant="title-medium" className={classes.description}>
                    {description}
                  </Text>
                )}
              </div>
            </div>

            <div className={classes.ticketContainer}>
              <Carousel
                align="center"
                slideSize={273}
                /* eslint-disable-line local-rules/use-theme-tokens */ slideGap={16}
                draggable
                speed={3}
                withIndicators={false}
                withControls={false}
                plugins={[autoplay.current]}
                classNames={{
                  viewport: classes.viewport,
                  container: classes.carouselContainer,
                }}
              >
                {bonuses.promotions.map((promotion) => (
                  <Carousel.Slide key={promotion.id}>
                    <PromoTicketCard {...promotion} />
                  </Carousel.Slide>
                ))}
              </Carousel>
            </div>

            <div className={classes.countdownContainer}>
              {bonuses.expiresAt && <BonusCountdown expirationDate={new Date(bonuses.expiresAt)} />}
            </div>

            <div className={classes.actionSection}>
              <Button
                variant="primary-fill"
                size="large"
                fullWidth
                onClick={onAction}
                data-test-id={actionButtonTestId}
              >
                {isLoading ? (
                  <Stack align="center" justify="center">
                    <Loader color="black" size={20} />
                  </Stack>
                ) : (
                  actionButtonLabel
                )}
              </Button>
              {error && (
                <Text
                  variant="title-small"
                  className={classes.error}
                  data-test-id={dt.promotions.bonusModal.error}
                >
                  {error}
                </Text>
              )}
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
}
